import React, { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { API_CONFIG } from "../config/api";
import { io } from "socket.io-client";
import { useBilling } from "../contexts/BillingContext";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, isLoading } = useAuth();
  const location = useLocation();
  const { data: billing, refresh, setHasActive } = useBilling();

  useEffect(() => {
    if (user) refresh();
  }, [user, refresh]);

  useEffect(() => {
    if (!user || !API_CONFIG.REALTIME_ENABLED) return;
    const socket = io(`${API_CONFIG.WS_BASE_URL}/user`, {
      path: "/socket.io",
      withCredentials: true,
      transports: ["websocket", "polling"],
    });
    socket.on("connect", () => {
      socket.emit("user:join", { userId: user.id });
    });
    socket.on(
      "subscription:updated",
      (payload?: { hasActive?: boolean; subscription?: any }) => {
        if (typeof payload?.hasActive === "boolean") {
          setHasActive(payload.hasActive);
        } else {
          refresh().catch(() => setHasActive(false));
        }
      }
    );
    return () => {
      socket.close();
    };
  }, [user, refresh, setHasActive]);

  // HTTP polling fallback when realtime is disabled
  useEffect(() => {
    if (!user || API_CONFIG.REALTIME_ENABLED) return;
    refresh();
    const interval = setInterval(refresh, 60000);
    return () => clearInterval(interval);
  }, [user, refresh]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (billing?.hasActive == null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  // Если подписка стала активной, а пользователь на странице выбора плана – уводим на главную
  if (billing?.hasActive === true && location.pathname === "/choose-plan") {
    return <Navigate to="/" replace />;
  }

  // Allow billing route itself without active subscription to avoid redirect loop
  if (billing?.hasActive === false && location.pathname !== "/choose-plan") {
    // Если бэкенд временно недоступен и ранее у нас не было данных, не уводим на редирект
    // (здесь предполагаем, что BillingProvider не затирает прошлое состояние)
    return <Navigate to="/choose-plan" replace />;
  }

  return <>{children}</>;
};
